import { DataTable } from '@/components/features/shared/DataTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Collector, CollectorStatus, formatCollectorStatus } from '@/domain/collectors/collector'
import { formatCurrency } from '@/lib/utils'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { MoreHorizontal, Pencil, Trash2, TrendingUp } from 'lucide-react'

interface SellerTableProps {
  sellers: Collector[]
  totalSellers?: number
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  searchKey?: string
  searchPlaceholder: string
  onCreateSeller?: () => void
  onEditSeller?: (seller: Collector) => void
  onDeleteSeller?: (sellerId: string) => void
  onRowClick?: (seller: Collector) => void
}

export const SellerTable = ({
  sellers,
  totalSellers,
  pagination,
  onPaginationChange,
  searchKey,
  searchPlaceholder,
  onCreateSeller,
  onEditSeller,
  onDeleteSeller,
  onRowClick,
}: SellerTableProps) => {
  const columns: ColumnDef<Collector>[] = [
    {
      accessorKey: 'code',
      header: 'Codigo',
      cell: ({ row }) => (
        <Badge variant="outline" className="bg-gray-50 font-mono">
          {row.original.code}
        </Badge>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Nombre',
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    // {
    //   accessorKey: 'email',
    //   header: 'Email',
    //   cell: ({ row }) => <div className="text-sm">{row.original.email || '-'}</div>,
    // },
    {
      accessorKey: 'docNumber',
      header: 'CUIT',
      cell: ({ row }) => <div className="text-sm">{row.original.docNumber || '-'}</div>,
    },
    {
      accessorKey: 'phone',
      header: 'Teléfono',
      cell: ({ row }) => <div className="text-sm text-wrap max-w-24">{row.original.phone || '-'}</div>,
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const status = row.original.status
        return (
          <Badge variant="outline" className={status === CollectorStatus.Active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-600'}>
            {formatCollectorStatus(status)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'contractsCount',
      header: 'Contratos',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{row.original.contractsCount}</span>
        </div>
      ),
    },
    {
      accessorKey: 'totalContractsAmount',
      header: 'Total Vendido',
      cell: ({ row }) => (
        <div className="text-sm font-mono">
          {formatCurrency(row.original.totalContractsAmount)}
        </div>
      ),
    },
    {
      accessorKey: 'commissionAmount',
      header: 'Comisión',
      cell: ({ row }) => (
        <div className="text-sm font-mono text-emerald-600">
          {formatCurrency(row.original.commissionAmount)}
        </div>
      ),
    },
    {
      accessorKey: 'sellCommission',
      header: '% Venta',
      cell: ({ row }) => <div className="text-sm">{row.original.sellCommission}%</div>,
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const seller = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditSeller?.(seller)}>
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDeleteSeller?.(seller.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Vendedores</h2>
          <p className="text-sm text-muted-foreground">
            Personal de ventas y promocion de los servicios
          </p>
        </div>
        <Button onClick={onCreateSeller}>Nuevo Colaborador</Button>
      </div>
      <DataTable
        columns={columns}
        data={sellers}
        searchKey={searchKey}
        searchPlaceholder={searchPlaceholder}
        totalRecords={totalSellers}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        onRowClick={onRowClick}
      />
    </div>
  )
}
